import React from "react"
import {FormProvider, useForm} from "react-hook-form"
import {Trans, useTranslation} from "react-i18next"
import {toast} from "react-toastify"
import {ChevronLeftIcon} from "@heroicons/react/20/solid"
import {zodResolver} from "@hookform/resolvers/zod"
import {z} from "zod"

import {ButtonForm} from "../../../components/Button.tsx"
import {CheckboxConnected} from "../../../components/fields/Checkbox.tsx"
import {FieldErrorMessage} from "../../../components/fields/components.tsx"
import {InputField} from "../../../components/fields/Input.tsx"
import {HeroLayout} from "../../../components/HeroLayout.tsx"
import {LayoutCard} from "../../../components/LayoutCard.tsx"
import {Link} from "../../../components/Link.tsx"
import {i18n} from "../../../i18n.ts"
import {useRegisterSalespersonMutation, useRegisterSalespersonResendMutation} from "../../../queries/salespeople.ts"
import requestError from "../../../services/requestError.tsx"
import {EGTMEvent, emitGTMEvent} from "../../../utils/gtm.tsx"
import {useDocumentTitle} from "../../../utils/hooks.tsx"
import {commonTransComponents} from "../../../utils/i18n.tsx"
import {
  getApiValidationMessages,
  isAxiosValidationError,
  requiredFieldMessage,
  setFormErrorsFromAxios,
  validateEmail,
} from "../../../utils/validation.ts"

const validationSchema = z.object({
  email: validateEmail(),
  tacAgreement: z.literal(true, {error: requiredFieldMessage}),
})

export const EmailFormPage: React.FC = () => {
  const {t} = useTranslation()

  useDocumentTitle(t("Signup_DocumentTitle"))

  const [sentToEmail, setSentToEmail] = React.useState<null | string>(null)

  return sentToEmail ? <EmailSent email={sentToEmail} /> : <EmailForm onSent={setSentToEmail} />
}

export const EmailForm: React.FC<{onSent: (email: string) => void}> = ({onSent}) => {
  const {t} = useTranslation()

  const methods = useForm<z.infer<typeof validationSchema>>({
    mode: "onTouched",
    resolver: zodResolver(validationSchema),
  })
  const {getValues, setError} = methods

  const registerSalespersonMutation = useRegisterSalespersonMutation()
  const registerSalespersonResendMutation = useRegisterSalespersonResendMutation()

  const handleSubmit = React.useCallback(async () => {
    const {email} = getValues()

    try {
      await registerSalespersonMutation.mutateAsync({email})
      emitGTMEvent({
        event: EGTMEvent.SALES_SIGNUP_SEND,
        email,
      })
      onSent(email)
    } catch (error) {
      if (!isAxiosValidationError(error)) {
        return requestError(error)
      }

      const emailErrorMessages = getApiValidationMessages(error.response.data.errors, "email")
      const isEmailTaken = emailErrorMessages.some(
        // TODO: this approach is terrible but has to be fixed on the BE
        message => message === "has already been taken" || message === "ste už použili"
      )

      if (isEmailTaken) {
        try {
          await registerSalespersonResendMutation.mutateAsync({email})
          onSent(email)
        } catch (realError) {
          setFormErrorsFromAxios(realError, setError)
        }
      } else {
        setFormErrorsFromAxios(error, setError)
      }
    }
  }, [getValues, onSent, registerSalespersonMutation, registerSalespersonResendMutation, setError])

  return (
    <HeroLayout
      leftAction={
        <Link to={"/sign-up"} variant={"neutral"} flipUnderline className={"inline-flex items-baseline gap-1"}>
          <ChevronLeftIcon className={"size-5 self-center"} />
          <span>{i18n.t("Signup_BackLink")}</span>
        </Link>
      }
    >
      <LayoutCard step={1} total={3} title={t("Signup_Salesperson_EmailFormPage_Title")}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleSubmit)}>
            <InputField
              name={"email"}
              type={"email"}
              variant={"medium"}
              label={t("Signup_Salesperson_EmailFormPage_EmailLabel")}
              placeholder={t("Signup_Salesperson_EmailFormPage_EmailPlaceholder")}
            />

            <div className={"my-2"}>
              <CheckboxConnected name={"tacAgreement"}>
                <Trans
                  i18nKey={"Signup_Salesperson_EmailFormPage_Terms"}
                  components={{
                    privacyLink: <Link flipUnderline to={t("Url_PrivacyPolicy")} target={"_blank"} />,
                  }}
                />
              </CheckboxConnected>
              <FieldErrorMessage name={"tacAgreement"} reserveSpace />
            </div>

            <ButtonForm fullWidth type={"submit"}>
              {t("Signup_Salesperson_EmailFormPage_SubmitButton")}
            </ButtonForm>
          </form>
        </FormProvider>
        <div className={"text-center text-sm"}>
          <Trans i18nKey={"Signup_Login"} components={{loginLink: <Link to={"/log-in"} flipUnderline />}} />
        </div>
      </LayoutCard>
    </HeroLayout>
  )
}

export const EmailSent: React.FC<{email: string}> = ({email}) => {
  const {t} = useTranslation()

  const registerSalespersonResendMutation = useRegisterSalespersonResendMutation()

  const handleResend = React.useCallback(async () => {
    try {
      await registerSalespersonResendMutation.mutateAsync({email})

      toast.success(t("Signup_Salesperson_EmailFormPage_Sent_ResentToast"))
    } catch {
      toast.error(t("Signup_Salesperson_EmailFormPage_Sent_ResentErrorToast"))
    }
  }, [email, registerSalespersonResendMutation, t])

  return (
    <HeroLayout>
      <LayoutCard step={2} total={3} title={t("Signup_Salesperson_EmailFormPage_Sent_Title")}>
        <div>
          <Trans
            i18nKey={"Signup_Salesperson_EmailFormPage_Sent_Text"}
            values={{email}}
            components={{...commonTransComponents, highlight: <strong className={"font-semibold text-cr-blue"} />}}
          />
        </div>
        <div className={"flex items-baseline gap-1"}>
          {t("Signup_Salesperson_EmailFormPage_Sent_ResendText")}{" "}
          <Link disabled={registerSalespersonResendMutation.isPending} onClick={handleResend}>
            {t("Signup_Salesperson_EmailFormPage_Sent_ResendButton")}
          </Link>
        </div>
      </LayoutCard>
    </HeroLayout>
  )
}
